/**
 * Food Log Page — daily meal tracker with AI macro estimation.
 *
 * Free users: see today + last 7 days, log freely.
 * Premium users: unlimited history + 14-day trend chart.
 *
 * Two entry paths:
 *  1. "Log from plan" — one-tap pull from today's nutrition plan
 *  2. "Quick log" — type meal name/description; AI estimates macros
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Plus, Camera, Trash2, ArrowLeft, ChevronLeft, ChevronRight,
  Sparkles, Lock, X, BookmarkPlus, Image as ImageIcon, TrendingUp,
  Lightbulb, Calculator,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { WaterCard } from '../components/water/WaterCard';
import { MacroCalculatorDialog } from '../components/food-log/MacroCalculatorDialog';
import { PhotoLightbox } from '../components/food-log/PhotoLightbox';
import { resizeImageFile } from '../utils/imageResize';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack'];

const MealRing = ({ label, current, target, color }) => {
  const pct = target > 0 ? Math.min(100, (current / target) * 100) : 0;
  return (
    <div className="flex flex-col items-center" data-testid={`macro-ring-${label.toLowerCase()}`}>
      <div className="relative h-16 w-16 sm:h-20 sm:w-20">
        <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90">
          <circle cx="18" cy="18" r="15.91" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
          <circle
            cx="18" cy="18" r="15.91" fill="none"
            stroke={color} strokeWidth="3" strokeLinecap="round"
            strokeDasharray={`${pct}, 100`}
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-heading text-sm sm:text-base text-white">{Math.round(current)}</span>
          <span className="text-[9px] text-muted-foreground -mt-0.5">/{Math.round(target)}</span>
        </div>
      </div>
      <span className="font-heading text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{label}</span>
    </div>
  );
};

const QuickAddDialog = ({ open, onClose, onSaved, defaultMealType, prefilledDescription, autoEstimateOnOpen, editEntry, logDate }) => {
  const { token } = useAuth();
  // Existing entries store TOTAL macros + a `servings` multiplier. The macro
  // input fields below represent PER-SERVING values, so on edit we divide the
  // stored totals back down. Defaults to 1 when missing (legacy entries).
  const initialServings = editEntry?.servings && editEntry.servings > 0 ? editEntry.servings : 1;
  const _perServ = (totalVal) => {
    if (!totalVal) return '';
    return String(Math.round((Number(totalVal) / initialServings) * 10) / 10);
  };
  const [mealType, setMealType] = useState(editEntry?.meal_type || defaultMealType || 'breakfast');
  const [description, setDescription] = useState(editEntry?.description || prefilledDescription || '');
  const [name, setName] = useState(editEntry?.name || '');
  const [servings, setServings] = useState(initialServings);
  const [calories, setCalories] = useState(_perServ(editEntry?.calories));
  const [protein, setProtein] = useState(_perServ(editEntry?.protein_g));
  const [carbs, setCarbs] = useState(_perServ(editEntry?.carbs_g));
  const [fat, setFat] = useState(_perServ(editEntry?.fat_g));
  const [photoUrl, setPhotoUrl] = useState(editEntry?.photo_url || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiPhotoBusy, setAiPhotoBusy] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Autocomplete — suggests previously logged meals as the user types.
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionActiveIdx, setSuggestionActiveIdx] = useState(-1);
  const suggestionsAbortRef = useRef(null);
  const suggestionsTimerRef = useRef(null);

  useEffect(() => {
    // Debounced fetch — cancels any in-flight request when the user keeps
    // typing so we never render stale results.
    if (!token) return;
    if (suggestionsTimerRef.current) clearTimeout(suggestionsTimerRef.current);
    const q = (description || '').trim();
    if (q.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    suggestionsTimerRef.current = setTimeout(async () => {
      try {
        if (suggestionsAbortRef.current) suggestionsAbortRef.current.abort();
        const ctrl = new AbortController();
        suggestionsAbortRef.current = ctrl;
        const res = await axios.get(`${BACKEND}/api/food-log/suggestions`, {
          params: { q, limit: 6 },
          withCredentials: true,
          signal: ctrl.signal,
        });
        const list = res.data?.suggestions || [];
        // Skip suggestions that are effectively the same as what the user just
        // typed — no point offering "eggs" when they've typed "eggs".
        const filtered = list.filter((s) => s.description.trim().toLowerCase() !== q.toLowerCase());
        setSuggestions(filtered);
        setShowSuggestions(filtered.length > 0);
      } catch (err) {
        if (!axios.isCancel(err) && err?.name !== 'CanceledError') {
          // Silent — autocomplete isn't critical.
        }
      }
    }, 220);
    return () => {
      if (suggestionsTimerRef.current) clearTimeout(suggestionsTimerRef.current);
    };
  }, [description, token]);

  const applySuggestion = (s) => {
    setDescription(s.description || '');
    if (s.name) setName(s.name);
    setCalories(String(Math.round(s.calories || 0)));
    setProtein(String(Math.round(s.protein_g || 0)));
    setCarbs(String(Math.round(s.carbs_g || 0)));
    setFat(String(Math.round(s.fat_g || 0)));
    setShowSuggestions(false);
    setSuggestionActiveIdx(-1);
    toast.success('Filled from your history', { icon: '↩️' });
  };

  // When the dialog was opened by tapping "Log this swap", run the AI estimator
  // automatically so the macros are pre-filled — user just confirms.
  useEffect(() => {
    if (open && autoEstimateOnOpen && prefilledDescription && !calories) {
      let cancelled = false;
      (async () => {
        try {
          const fd = { description: prefilledDescription };
          const res = await axios.post(
            `${BACKEND}/api/food-log/entries/estimate-only`,
            fd,
            { withCredentials: true, headers: { Authorization: token ? `Bearer ${token}` : undefined } }
          );
          if (cancelled) return;
          if (res.data?.name && !name) setName(res.data.name);
          if (res.data?.calories) setCalories(String(res.data.calories));
          if (res.data?.protein_g) setProtein(String(res.data.protein_g));
          if (res.data?.carbs_g) setCarbs(String(res.data.carbs_g));
          if (res.data?.fat_g) setFat(String(res.data.fat_g));
        } catch {
          /* silent — user can still type macros manually */
        }
      })();
      return () => { cancelled = true; };
    }
  }, [open, autoEstimateOnOpen, prefilledDescription]);

  const handlePhoto = async (e) => {
    const original = e.target.files?.[0];
    if (!original) return;
    setUploading(true);
    try {
      // Resize/compress browser-side first so big phone photos don't blow up.
      const file = await resizeImageFile(original);
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${BACKEND}/api/food-log/upload-photo`, fd, {
        withCredentials: true,
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      const uploadedUrl = res.data.photo_url;
      setPhotoUrl(uploadedUrl);
      // After upload, auto-fire vision-based macro estimation. Only pre-fill
      // fields that are still empty — never stomp on the user's manual edits.
      if (uploadedUrl && !editEntry) {
        setAiPhotoBusy(true);
        try {
          const v = await axios.post(`${BACKEND}/api/food-log/entries/estimate-from-photo`, {
            photo_url: uploadedUrl,
            hint: description.trim() || null,
          }, { withCredentials: true, headers: { Authorization: token ? `Bearer ${token}` : undefined } });
          const data = v.data || {};
          if (data.name && !name) setName(data.name);
          if (data.description && !description) setDescription(data.description);
          if (data.calories && !calories) setCalories(String(Math.round(data.calories)));
          if (data.protein_g && !protein) setProtein(String(Math.round(data.protein_g)));
          if (data.carbs_g && !carbs) setCarbs(String(Math.round(data.carbs_g)));
          if (data.fat_g && !fat) setFat(String(Math.round(data.fat_g)));
          if (data.confidence === 'low') {
            toast.message('AI wasn\u2019t sure — please review and edit before saving.');
          } else if (data.calories) {
            toast.success(`AI estimated ~${Math.round(data.calories)} cal`);
          }
        } catch {
          /* silent — manual edit still works */
        }
        setAiPhotoBusy(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleSave = async (useAI = false) => {
    if (!description.trim() && !name.trim()) {
      toast.error('Tell me what you ate first');
      return;
    }
    setSaving(true);
    try {
      // Multiply per-serving macros by servings to get the total. AI estimation
      // also returns per-serving values so the multiplier still applies cleanly.
      const qty = servings && servings > 0 ? servings : 1;
      const mult = (s) => (s ? parseFloat(s) * qty : null);
      const payload = {
        meal_type: mealType,
        name: name.trim() || null,
        description: description.trim(),
        calories: mult(calories),
        protein_g: mult(protein),
        carbs_g: mult(carbs),
        fat_g: mult(fat),
        servings: qty,
        photo_url: photoUrl || null,
        source: useAI ? 'freeform' : 'manual',
        estimate_with_ai: useAI,
        // Pin the entry to the user's locally-selected date so it never
        // lands on the next UTC day when the user logs late at night.
        log_date: logDate || null,
      };
      if (editEntry) {
        // PATCH ignores source / estimate_with_ai — pass only the editable fields
        const patch = {
          meal_type: mealType,
          name: name.trim() || null,
          description: description.trim(),
          calories: payload.calories,
          protein_g: payload.protein_g,
          carbs_g: payload.carbs_g,
          fat_g: payload.fat_g,
          servings: qty,
          photo_url: payload.photo_url,
        };
        await axios.patch(`${BACKEND}/api/food-log/entries/${editEntry.id}`, patch, {
          withCredentials: true,
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        });
        toast.success('Meal updated');
      } else {
        await axios.post(`${BACKEND}/api/food-log/entries`, payload, {
          withCredentials: true,
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        });
        toast.success('Meal logged');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 overflow-y-auto" onClick={onClose} data-testid="quick-add-dialog">
      <PhotoLightbox src={lightboxOpen ? photoUrl : null} alt="Meal photo" onClose={() => setLightboxOpen(false)} />
      <div onClick={(e) => e.stopPropagation()} className="bg-card border-2 border-primary/40 rounded-2xl max-w-lg w-full my-auto p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-white uppercase tracking-wider">{editEntry ? 'Edit Meal' : 'Log a Meal'}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-white" data-testid="quick-add-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-3">
          <div className="flex gap-1.5 flex-wrap">
            {MEAL_TYPES.map((m) => (
              <button
                key={m}
                onClick={() => setMealType(m)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-md border ${
                  mealType === m ? 'bg-primary text-black border-primary' : 'bg-card text-muted-foreground border-border'
                }`}
                data-testid={`meal-type-${m}`}
              >{m}</button>
            ))}
          </div>

          <div className="relative">
            <label className="text-xs uppercase tracking-wider text-muted-foreground">What did you eat?</label>
            <Input
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setSuggestionActiveIdx(-1);
                setShowSuggestions(true);
              }}
              onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
              onKeyDown={(e) => {
                if (!showSuggestions || suggestions.length === 0) return;
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setSuggestionActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setSuggestionActiveIdx((i) => Math.max(i - 1, 0));
                } else if (e.key === 'Enter' && suggestionActiveIdx >= 0) {
                  e.preventDefault();
                  applySuggestion(suggestions[suggestionActiveIdx]);
                } else if (e.key === 'Escape') {
                  setShowSuggestions(false);
                }
              }}
              placeholder="e.g. 2 eggs + toast + coffee"
              className="bg-card border-border text-white mt-1"
              data-testid="quick-add-description"
              autoComplete="off"
            />
            <p className="text-[10px] text-muted-foreground mt-1">Tap &quot;AI Estimate&quot; to fill macros automatically.</p>

            {/* Autocomplete dropdown — reuses previously-logged meals so
                repeat meals are one tap instead of a full retype. */}
            {showSuggestions && suggestions.length > 0 && (
              <div
                className="absolute z-30 left-0 right-0 mt-1 rounded-xl border border-primary/30 bg-black/95 backdrop-blur-md shadow-[0_8px_30px_rgba(0,0,0,0.45)] overflow-hidden"
                data-testid="quick-add-suggestions"
                onMouseDown={(e) => e.preventDefault()}
              >
                <div className="px-3 py-1.5 text-[9px] uppercase tracking-[0.2em] text-primary/80 border-b border-white/[0.06] bg-primary/[0.04]">
                  From your history · tap to auto-fill
                </div>
                {suggestions.map((s, i) => (
                  <button
                    key={`${s.description}-${i}`}
                    type="button"
                    onClick={() => applySuggestion(s)}
                    onMouseEnter={() => setSuggestionActiveIdx(i)}
                    className={`w-full text-left px-3 py-2 flex items-center gap-3 transition-colors border-b border-white/[0.03] last:border-b-0 ${
                      suggestionActiveIdx === i ? 'bg-primary/[0.08]' : 'hover:bg-white/[0.04]'
                    }`}
                    data-testid={`suggestion-${i}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{s.description}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {Math.round(s.calories)} cal · {Math.round(s.protein_g)}P · {Math.round(s.carbs_g)}C · {Math.round(s.fat_g)}F <span className="text-white/30">/ serving</span>
                      </p>
                    </div>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-primary bg-primary/[0.1] border border-primary/30 rounded-full px-2 py-0.5 flex-shrink-0">
                      Fill
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-4 gap-2">
            {[
              { label: 'cal', val: calories, set: setCalories },
              { label: 'P (g)', val: protein, set: setProtein },
              { label: 'C (g)', val: carbs, set: setCarbs },
              { label: 'F (g)', val: fat, set: setFat },
            ].map(({ label, val, set }) => (
              <div key={label}>
                <label className="text-[10px] uppercase text-muted-foreground">{label}</label>
                <Input
                  type="number"
                  value={val}
                  onChange={(e) => set(e.target.value)}
                  placeholder="0"
                  className="bg-card border-border text-white text-sm mt-0.5"
                  data-testid={`quick-add-${label.replace(/[^a-z]/gi, '').toLowerCase()}`}
                />
              </div>
            ))}
          </div>

          {/* Servings stepper — multiplies the per-serving macros above so
              users can log "2 slices" or "1.5 cups" in one shot. */}
          <div className="flex items-center justify-between bg-card/60 border border-border rounded-lg px-3 py-2">
            <div className="flex flex-col">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Servings</span>
              <span className="text-[10px] text-muted-foreground/70">macros above are per serving</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setServings((s) => Math.max(0.25, +(s - 0.5).toFixed(2)))}
                className="h-8 w-8 rounded-full border border-border text-white hover:bg-card flex items-center justify-center text-lg leading-none"
                data-testid="quick-add-servings-minus"
                aria-label="Decrease servings"
              >−</button>
              <input
                type="number"
                step="0.5"
                min="0.25"
                value={servings}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  setServings(Number.isFinite(v) && v > 0 ? v : 1);
                }}
                className="w-16 bg-card border border-border rounded text-white text-center text-sm py-1.5 focus:outline-none focus:border-primary"
                data-testid="quick-add-servings-input"
              />
              <button
                type="button"
                onClick={() => setServings((s) => +(s + 0.5).toFixed(2))}
                className="h-8 w-8 rounded-full border border-border text-white hover:bg-card flex items-center justify-center text-lg leading-none"
                data-testid="quick-add-servings-plus"
                aria-label="Increase servings"
              >+</button>
            </div>
          </div>
          {servings !== 1 && (calories || protein || carbs || fat) && (
            <p className="text-[11px] text-primary text-center -mt-1" data-testid="quick-add-servings-preview">
              Total ×{servings}: {Math.round((parseFloat(calories) || 0) * servings)} cal · P {Math.round((parseFloat(protein) || 0) * servings)}g · C {Math.round((parseFloat(carbs) || 0) * servings)}g · F {Math.round((parseFloat(fat) || 0) * servings)}g
            </p>
          )}

          {/* Photo */}
          <div className="flex items-center gap-3">
            {photoUrl ? (
              <div className="relative">
                <img
                  src={photoUrl}
                  alt=""
                  className="h-16 w-16 rounded-lg object-cover border border-border cursor-zoom-in hover:opacity-90 transition-opacity"
                  onClick={() => setLightboxOpen(true)}
                  data-testid="quick-add-photo-preview"
                />
                <button
                  onClick={() => setPhotoUrl('')}
                  className="absolute -top-1 -right-1 bg-black border border-border rounded-full p-0.5 text-muted-foreground hover:text-red-400"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <div className="h-16 w-16 rounded-lg border border-dashed border-border flex items-center justify-center text-muted-foreground">
                <ImageIcon className="h-5 w-5" />
              </div>
            )}
            {/* Visually-hidden but DOM-clickable input — display:none breaks the
                picker on some Android browsers. The button below triggers it
                via ref so we never depend on a wrapped <label>. */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhoto}
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
              data-testid="quick-add-photo-input"
            />
            {/* Dedicated camera-capture input — `capture="environment"` hints
                mobile browsers to open the rear camera directly instead of the
                gallery picker. */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePhoto}
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
              data-testid="quick-add-camera-input"
            />
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => cameraInputRef.current && cameraInputRef.current.click()}
                disabled={uploading || aiPhotoBusy}
                className="inline-flex items-center gap-2 px-3 py-2 border border-border text-white text-xs rounded hover:bg-card/80 disabled:opacity-60"
                data-testid="quick-add-camera-btn"
              >
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : aiPhotoBusy ? <Sparkles className="h-3.5 w-3.5 text-blue-400 animate-pulse" /> : <Camera className="h-3.5 w-3.5" />}
                {uploading ? 'Uploading\u2026' : aiPhotoBusy ? 'AI is reading your meal\u2026' : 'Take photo'}
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                disabled={uploading || aiPhotoBusy}
                className="inline-flex items-center gap-2 px-3 py-2 border border-border text-white text-xs rounded hover:bg-card/80 disabled:opacity-60"
                data-testid="quick-add-photo-btn"
              >
                <ImageIcon className="h-3.5 w-3.5" />
                {photoUrl ? 'Replace from library' : 'Upload from library'}
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mt-5">
          {editEntry ? (
            <Button
              onClick={() => handleSave(false)}
              disabled={saving}
              className="flex-1 bg-primary text-black hover:bg-primary/90"
              data-testid="quick-add-edit-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save changes
            </Button>
          ) : (
            <>
              <Button
                onClick={() => handleSave(true)}
                disabled={saving || !description.trim()}
                className="flex-1 bg-blue-600 hover:bg-blue-500 text-white"
                data-testid="quick-add-ai-save"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                AI Estimate & Save
              </Button>
              <Button
                onClick={() => handleSave(false)}
                disabled={saving}
                variant="outline"
                className="flex-1"
                data-testid="quick-add-manual-save"
              >
                Save without AI
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const PlanMealsDialog = ({ open, onClose, onSaved, logDate }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [savingIdx, setSavingIdx] = useState(null);

  // Fetch on mount — because parent only renders this when open=true and uses a
  // fresh key, we get a clean fetch every time the user opens the dialog.
  useEffect(() => {
    let alive = true;
    axios.get(`${BACKEND}/api/food-log/plan-today`, { withCredentials: true, headers: { Authorization: token ? `Bearer ${token}` : undefined } })
      .then((res) => { if (alive) setData(res.data); })
      .catch(() => { if (alive) setData({ has_plan: false, meals: [] }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const logMeal = async (meal, idx) => {
    setSavingIdx(idx);
    try {
      const mealType = (meal.meal_type || meal.type || meal.name || '').toLowerCase();
      const normalized = MEAL_TYPES.find((t) => mealType.includes(t)) || 'snack';
      await axios.post(`${BACKEND}/api/food-log/entries`, {
        meal_type: normalized,
        name: meal.name || meal.title || `Meal ${idx + 1}`,
        description: meal.description || '',
        calories: meal.calories || 0,
        protein_g: meal.protein_g || meal.protein || 0,
        carbs_g: meal.carbs_g || meal.carbs || 0,
        fat_g: meal.fat_g || meal.fat || 0,
        source: 'plan',
        log_date: logDate || null,
        plan_meal_ref: {
          plan_id: data.plan_id, week_index: data.week_index, day_index: data.day_index, meal_index: idx,
        },
      }, { withCredentials: true, headers: { Authorization: token ? `Bearer ${token}` : undefined } });
      toast.success('Logged from plan');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSavingIdx(null);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 overflow-y-auto" onClick={onClose} data-testid="plan-meals-dialog">
      <div onClick={(e) => e.stopPropagation()} className="bg-card border-2 border-primary/40 rounded-2xl max-w-lg w-full my-auto p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-white uppercase tracking-wider">Log From Your Plan</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : !data?.has_plan ? (
          <div className="text-center text-muted-foreground py-8">
            <BookmarkPlus className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No active nutrition plan yet.</p>
            <Link to="/nutrition" className="text-primary text-sm underline mt-2 inline-block" onClick={onClose}>
              Generate one →
            </Link>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Today&apos;s plan · {data.day_label}
            </p>
            {(data.meals || []).map((meal, i) => (
              <div key={meal.id || `${meal.name || meal.title || 'meal'}-${i}`} className="border border-border rounded-lg p-3 flex items-start justify-between gap-3" data-testid={`plan-meal-${i}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-white font-medium text-sm">{meal.name || meal.title || `Meal ${i + 1}`}</p>
                  <p className="text-muted-foreground text-xs">
                    {Math.round(meal.calories || 0)} cal · P {Math.round(meal.protein_g || meal.protein || 0)}g · C {Math.round(meal.carbs_g || meal.carbs || 0)}g · F {Math.round(meal.fat_g || meal.fat || 0)}g
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => logMeal(meal, i)}
                  disabled={savingIdx === i}
                  className="bg-primary text-black hover:bg-primary/90 shrink-0"
                  data-testid={`plan-meal-log-${i}`}
                >
                  {savingIdx === i ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Log'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const FoodLogPage = () => {
  const { token } = useAuth();
  // Use the user's LOCAL calendar date (not UTC) so the date strip reflects
  // their actual day. UTC caused the "Today" label to lag for users in
  // western timezones across midnight.
  const localToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const [logDate, setLogDate] = useState(() => localToday());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const [defaultMealType, setDefaultMealType] = useState('breakfast');
  const [swapPrefill, setSwapPrefill] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showCalc, setShowCalc] = useState(false);
  const [trends, setTrends] = useState(null);
  const [showTrends, setShowTrends] = useState(false);
  const [coach, setCoach] = useState(null);
  const [coachLoading, setCoachLoading] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  const fetchDay = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND}/api/food-log/day`, {
        params: { date: d },
        withCredentials: true,
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('Upgrade to premium to view dates older than 7 days');
      } else {
        toast.error('Failed to load food log');
      }
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchDay(logDate); }, [logDate, fetchDay]);

  // Coach: only meaningful for "today" — past-day overages are water under the
  // bridge. Inlined into the effect (no useCallback indirection) so the lint
  // analyzer sees a clean setState-in-effect path it can reason about.
  useEffect(() => {
    let alive = true;
    const today = logDate === localToday();
    if (!today) {
      setCoach(null);
      return undefined;
    }
    setCoachLoading(true);
    axios.get(`${BACKEND}/api/food-log/coach/check`, {
      withCredentials: true,
      headers: { Authorization: token ? `Bearer ${token}` : undefined },
    })
      .then((res) => { if (alive) setCoach(res.data); })
      .catch(() => { if (alive) setCoach(null); })
      .finally(() => { if (alive) setCoachLoading(false); });
    return () => { alive = false; };
  }, [data, logDate, token]);

  const handleDismissCoach = async () => {
    try {
      await axios.post(`${BACKEND}/api/food-log/coach/dismiss`, {}, {
        withCredentials: true,
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      setCoach((c) => ({ ...c, suggestion: null, dismissed_today: true }));
    } catch {
      /* silent */
    }
  };

  // Derived state used by the JSX. Computed inline since the coach effect now
  // re-derives its own `today` flag.
  const isToday = logDate === localToday();

  const shiftDay = (delta) => {
    const dt = new Date(logDate + 'T00:00:00');
    dt.setDate(dt.getDate() + delta);
    setLogDate(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${BACKEND}/api/food-log/entries/${id}`, {
        withCredentials: true,
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      toast.success('Deleted');
      fetchDay(logDate);
    } catch {
      toast.error('Delete failed');
    }
  };

  const loadTrends = async () => {
    setShowTrends(true);
    try {
      const res = await axios.get(`${BACKEND}/api/food-log/trends?days=14`, {
        withCredentials: true,
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      setTrends(res.data);
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('Trends are a premium feature');
      } else {
        toast.error('Failed to load trends');
      }
      setShowTrends(false);
    }
  };

  const grouped = useMemo(() => {
    const g = { breakfast: [], lunch: [], dinner: [], snack: [] };
    (data?.entries || []).forEach((e) => {
      if (g[e.meal_type]) g[e.meal_type].push(e);
    });
    return g;
  }, [data]);

  // Flat ordered list of all photos for the day — feeds the lightbox gallery
  // so users can swipe between meals in display order (breakfast → snack).
  const dayPhotos = useMemo(() => {
    const out = [];
    MEAL_TYPES.forEach((m) => {
      grouped[m].forEach((entry) => {
        if (entry.photo_url) out.push({ id: entry.id, url: entry.photo_url, caption: entry.name });
      });
    });
    return out;
  }, [grouped]);
  const lightboxIndex = lightboxPhoto
    ? Math.max(0, dayPhotos.findIndex((p) => p.id === lightboxPhoto))
    : 0;

  const targets = data?.targets || { calories: 2200, protein_g: 140, carbs_g: 220, fat_g: 70 };
  const totals = data?.totals || { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 };
  const isPremium = data?.is_premium;

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="food-log-page">
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <header className="mb-6">
          <h1 className="font-heading text-4xl sm:text-5xl text-white uppercase tracking-wider">
            Food <span className="text-primary">Log</span>
          </h1>
          <p className="text-muted-foreground text-sm mt-2">
            Track what you eat — AI estimates macros so you don&apos;t have to do the math.
          </p>
        </header>

        {/* Date strip + Water card — merged into one cohesive panel. The date
            strip is rendered as the WaterCard's headerSlot so prev/next arrows
            sit visually adjacent to the bottle they control. */}
        <div className="mb-5">
          <WaterCard
            compact
            showOverviewLink
            date={logDate}
            headerSlot={
              <div className="flex items-center justify-between">
                <button
                  onClick={() => shiftDay(-1)}
                  className="text-muted-foreground hover:text-white"
                  data-testid="food-log-prev-day"
                  aria-label="Previous day"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div className="text-center">
                  <p className="font-heading text-white text-base uppercase tracking-wider">
                    {isToday ? 'Today' : new Date(logDate + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                  </p>
                  <p className="text-[11px] text-muted-foreground">{logDate}</p>
                </div>
                <button
                  onClick={() => shiftDay(1)}
                  disabled={isToday}
                  className="text-muted-foreground hover:text-white disabled:opacity-30"
                  data-testid="food-log-next-day"
                  aria-label="Next day"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            }
          />
        </div>

        {/* Macro rings */}
        <div className="bg-card border border-border rounded-2xl p-5 mb-5">
          <div className="flex items-center justify-between mb-4 gap-2">
            <p className="font-heading text-xs uppercase tracking-wider text-muted-foreground">
              Daily targets {targets.source === 'plan' ? '(from your plan)' : targets.source === 'custom' ? '(custom)' : '(default)'}
            </p>
            {isPremium ? (
              <Button size="sm" variant="outline" onClick={loadTrends} data-testid="food-log-trends-btn">
                <TrendingUp className="h-3.5 w-3.5 mr-1" /> Trends
              </Button>
            ) : (
              <Link to="/subscription" className="text-[10px] text-yellow-400 uppercase tracking-wider flex items-center gap-1">
                <Lock className="h-3 w-3" /> Premium for trends
              </Link>
            )}
          </div>
          <div className="grid grid-cols-4 gap-2">
            <MealRing label="Cal" current={totals.calories} target={targets.calories} color="#facc15" />
            <MealRing label="Protein" current={totals.protein_g} target={targets.protein_g} color="#22c55e" />
            <MealRing label="Carbs" current={totals.carbs_g} target={targets.carbs_g} color="#3b82f6" />
            <MealRing label="Fat" current={totals.fat_g} target={targets.fat_g} color="#f97316" />
          </div>
          {/* Macro calculator CTA — visible button so users don't miss it */}
          <button
            type="button"
            onClick={() => setShowCalc(true)}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-primary/15 hover:bg-primary/25 border border-primary/40 text-primary text-xs font-medium uppercase tracking-wider rounded-lg transition-colors"
            data-testid="food-log-open-calc"
          >
            <Calculator className="h-4 w-4" />
            {targets.source === 'custom' ? 'Edit my macro targets' : 'Calculate my macros'}
          </button>
        </div>

        {/* AI Coach suggestion — shows when any macro is >=120% of target */}
        {isToday && coach?.suggestion && (
          <div
            className="mb-5 bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/40 rounded-2xl p-4 relative"
            data-testid="food-log-coach-card"
          >
            <button
              onClick={handleDismissCoach}
              className="absolute top-3 right-3 text-muted-foreground hover:text-white"
              aria-label="Dismiss"
              data-testid="food-log-coach-dismiss"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-start gap-3 pr-6">
              <div className="h-9 w-9 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-300 shrink-0">
                <Lightbulb className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-heading text-sm text-white uppercase tracking-wider" data-testid="food-log-coach-title">
                  {coach.suggestion.title}
                </p>
                <p className="text-muted-foreground text-sm mt-1" data-testid="food-log-coach-message">
                  {coach.suggestion.message}
                </p>
                {coach.suggestion.swap_suggestion && (
                  <div className="mt-2 px-3 py-2 bg-black/30 border border-blue-500/30 rounded-lg flex items-start gap-2">
                    <Sparkles className="h-3.5 w-3.5 text-blue-300 mt-0.5 shrink-0" />
                    <span className="text-blue-200 text-xs flex-1" data-testid="food-log-coach-swap">
                      Try next: {coach.suggestion.swap_suggestion}
                    </span>
                  </div>
                )}
                {coach.suggestion.swap_suggestion && (
                  <button
                    onClick={() => {
                      // Pick the next logical meal type based on what's already today
                      const counts = { breakfast: 0, lunch: 0, dinner: 0, snack: 0 };
                      (data?.entries || []).forEach((e) => { counts[e.meal_type] = (counts[e.meal_type] || 0) + 1; });
                      const next = counts.breakfast === 0 ? 'breakfast'
                        : counts.lunch === 0 ? 'lunch'
                        : counts.dinner === 0 ? 'dinner'
                        : 'snack';
                      setDefaultMealType(next);
                      setSwapPrefill({ description: coach.suggestion.swap_suggestion });
                      setShowAdd(true);
                    }}
                    className="mt-2 w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-lg transition-colors"
                    data-testid="food-log-coach-log-swap"
                  >
                    <Plus className="h-3.5 w-3.5" /> Log this swap
                  </button>
                )}
                {coach.overages?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {coach.overages.map((o) => (
                      <Badge key={o.macro} className="bg-orange-500/20 text-orange-300 border-0 text-[10px] capitalize">
                        {o.macro} {Math.round(o.ratio * 100)}%
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Coach status when no suggestion is firing — keeps the UI quiet */}
        {coachLoading && !coach?.suggestion && (
          <div className="sr-only" data-testid="food-log-coach-loading">Checking…</div>
        )}

        {/* Actions */}
        <div className="flex gap-2 mb-5">
          <Button onClick={() => setShowPlan(true)} variant="outline" className="flex-1" data-testid="food-log-from-plan-btn">
            <BookmarkPlus className="h-4 w-4 mr-2" /> From Plan
          </Button>
          <Button onClick={() => { setDefaultMealType('breakfast'); setShowAdd(true); }} className="flex-1 bg-primary text-black hover:bg-primary/90" data-testid="food-log-quick-add-btn">
            <Plus className="h-4 w-4 mr-2" /> Quick Log
          </Button>
        </div>

        {/* Entries grouped by meal type */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          MEAL_TYPES.map((m) => (
            <section key={m} className="mb-4" data-testid={`food-log-section-${m}`}>
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-heading text-sm text-white uppercase tracking-wider">{m}</h2>
                <button
                  onClick={() => { setDefaultMealType(m); setShowAdd(true); }}
                  className="text-[10px] text-primary uppercase tracking-wider"
                  data-testid={`food-log-add-${m}`}
                >
                  + Add
                </button>
              </div>
              {grouped[m].length === 0 ? (
                <p className="text-muted-foreground text-xs italic py-2 px-3 bg-card/40 rounded-lg border border-border">No {m} logged yet</p>
              ) : (
                <div className="space-y-2">
                  {grouped[m].map((entry) => (
                    <div
                      key={entry.id}
                      onClick={() => { setEditingEntry(entry); setShowAdd(true); }}
                      className="bg-card border border-border rounded-lg p-3 flex items-start gap-3 cursor-pointer hover:border-primary/50 hover:bg-card/80 transition-colors"
                      data-testid={`food-log-entry-${entry.id}`}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') { setEditingEntry(entry); setShowAdd(true); } }}
                    >
                      {entry.photo_url ? (
                        <img
                          src={entry.photo_url}
                          alt=""
                          className="h-14 w-14 rounded object-cover border border-border shrink-0 cursor-zoom-in hover:opacity-90 transition-opacity"
                          onClick={(e) => { e.stopPropagation(); setLightboxPhoto(entry.id); }}
                          data-testid={`food-log-entry-photo-${entry.id}`}
                        />
                      ) : (
                        <div className="h-14 w-14 rounded bg-black/40 border border-border flex items-center justify-center text-muted-foreground shrink-0">
                          <ImageIcon className="h-5 w-5" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white font-medium text-sm truncate">{entry.name}</p>
                          {entry.servings && entry.servings !== 1 && (
                            <Badge className="bg-primary/20 text-primary border-0 text-[9px]" data-testid={`food-log-entry-servings-${entry.id}`}>
                              ×{entry.servings}
                            </Badge>
                          )}
                          {entry.source === 'plan' && <Badge className="bg-blue-500/20 text-blue-300 border-0 text-[9px]">PLAN</Badge>}
                          {entry.source === 'freeform' && <Badge className="bg-purple-500/20 text-purple-300 border-0 text-[9px]">AI</Badge>}
                        </div>
                        {entry.description && <p className="text-muted-foreground text-xs line-clamp-1">{entry.description}</p>}
                        <p className="text-muted-foreground text-xs mt-0.5">
                          <span className="text-yellow-400">{Math.round(entry.calories)}</span> cal · P {Math.round(entry.protein_g)}g · C {Math.round(entry.carbs_g)}g · F {Math.round(entry.fat_g)}g
                        </p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(entry.id); }}
                        className="text-muted-foreground hover:text-red-400 p-1 shrink-0"
                        data-testid={`food-log-delete-${entry.id}`}
                        aria-label="Delete entry"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))
        )}
      </main>

      {/* Dialogs are conditionally mounted so their useState initializers fire
          fresh on each open — no more setState-in-useEffect to mirror props. */}
      <PhotoLightbox
        photos={lightboxPhoto ? dayPhotos : []}
        index={lightboxIndex}
        onIndexChange={(i) => setLightboxPhoto(dayPhotos[i]?.id || null)}
        onClose={() => setLightboxPhoto(null)}
      />
      {showAdd && (
        <QuickAddDialog
          open
          onClose={() => { setShowAdd(false); setSwapPrefill(null); setEditingEntry(null); }}
          onSaved={() => fetchDay(logDate)}
          defaultMealType={defaultMealType}
          prefilledDescription={swapPrefill?.description}
          autoEstimateOnOpen={!!swapPrefill}
          editEntry={editingEntry}
          logDate={logDate}
        />
      )}
      {showPlan && (
        <PlanMealsDialog
          open
          onClose={() => setShowPlan(false)}
          onSaved={() => fetchDay(logDate)}
          logDate={logDate}
        />
      )}
      {showCalc && (
        <MacroCalculatorDialog
          open
          onClose={() => setShowCalc(false)}
          onSaved={() => fetchDay(logDate)}
        />
      )}

      {showTrends && trends && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4" onClick={() => setShowTrends(false)} data-testid="trends-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-card border-2 border-primary/40 rounded-2xl max-w-2xl w-full p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg text-white uppercase tracking-wider">14-Day Trend</h3>
              <button onClick={() => setShowTrends(false)} className="text-muted-foreground hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex items-end gap-1 h-40 px-2">
              {trends.points.map((p) => {
                const h = trends.targets.calories > 0 ? Math.min(100, (p.calories / trends.targets.calories) * 100) : 0;
                return (
                  <div key={p.date} className="flex-1 flex flex-col items-center justify-end" title={`${p.date}: ${Math.round(p.calories)} cal`}>
                    <div className="w-full bg-primary rounded-t" style={{ height: `${h}%`, minHeight: p.calories > 0 ? '4px' : '0' }} />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between text-[10px] text-muted-foreground mt-2 px-2">
              <span>{trends.points[0]?.date.slice(5)}</span>
              <span>Target: {trends.targets.calories} cal/day</span>
              <span>{trends.points[trends.points.length - 1]?.date.slice(5)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FoodLogPage;
